/**
 * @file    useChartMetadataFetch.ts
 * @scope   SHARED
 * @summary Owns the Guardrails metadata round-trips of the drawer: loading the chart-type catalog
 *          on mount and applying every chart Config that arrives on either axios channel.
 * @consumes Guardrails endpoints, `getChartTypesModel`, the two `useAxios` channels
 * @produces Nothing directly — it feeds the injected `onChartTypes` / `onChartConfig` callbacks.
 * @sideEffects One fetch on mount; reacts to both axios responses.
 * @notes Extracted verbatim from DiyDrawer so the orchestrator no longer owns three effects.
 *        The effects are registered in the SAME order as before, so execution order relative to
 *        the surrounding hooks is unchanged.
 */
import { useEffect } from 'react';
import { getChartTypesModel } from 'services/Torogoz/TogorozApi';
import { normalizeGuardRailsChartTypes } from 'utils/normalizeGuardRailsConfig';

/** Inputs of {@link useChartMetadataFetch}. */
export type UseChartMetadataFetchParams = {
  /** Environment endpoints (`getChartTypesModel` template). */
  endpoints: any;
  /** Workspace the chart-type catalog is scoped to. */
  workspace: any;
  /** `true` for Medusa-query widgets, which get a different catalog. */
  hasMedusaQuery: boolean;
  /** Consumer-mode flag forwarded to the API layer. */
  isConsumer: boolean;
  /** Primary axios channel fetcher (chart-type catalog + first chart Config). */
  axiosFetch: any;
  /** Primary axios channel response. */
  response: any;
  /** Secondary axios channel response, used when the chart type changes. */
  responseData: any;
  /** Called with the raw chart-types array once the catalog arrives. */
  onChartTypes: (types: any[]) => void;
  /**
   * Called with a single chart `Config`.
   *
   * @param config      Guardrails chart configuration.
   * @param withFilters `true` only for the secondary channel, which also refreshes chart filters.
   */
  onChartConfig: (config: any, withFilters: boolean) => void;
};

export const useChartMetadataFetch = ({
  endpoints,
  workspace,
  hasMedusaQuery,
  isConsumer,
  axiosFetch,
  response,
  responseData,
  onChartTypes,
  onChartConfig,
}: UseChartMetadataFetchParams): void => {
  useEffect(() => {
    // Mount-only: load the list of available chart types for this workspace.
    // WHY the two URLs: Medusa-query widgets are served a different chart-type catalog, so the
    // flag has to travel on the request rather than being filtered client-side afterwards.
    const getData = async () => {
      const url = hasMedusaQuery
        ? `${endpoints.getChartTypesModel}?hasMedusaQuery=true&workspaceId=${workspace}`
        : `${endpoints.getChartTypesModel}?workspaceId=${workspace}`;
      await getChartTypesModel(url, axiosFetch, isConsumer);
    };
    getData();
  }, []);

  useEffect(() => {
    // `response` carries two different payloads on the SAME axios channel:
    // an ARRAY = the chart-types catalog (initialize the dropdown), an OBJECT = a single chart's
    // Config (questions/collections/rules). WHY the shape switch: the Guardrails endpoint reuses
    // one response envelope for both, so we discriminate by `Array.isArray` here.
    if (!response) return;
    if (Array.isArray(response)) onChartTypes(normalizeGuardRailsChartTypes(response));
    else onChartConfig(response?.config, false);
  }, [response]);

  useEffect(() => {
    // `responseData` is a SEPARATE axios channel used when re-fetching a chart's config after the
    // type changes. WHY a second channel: it must not race with / clobber the primary `response`
    // above, which may still be resolving the chart-types catalog.
    if (!responseData) return;
    onChartConfig(responseData?.config, true);
  }, [responseData]);
};
